// frontend/src/components/EditPrinterModal.jsx

import React, { useState, useEffect } from 'react';
import { Save, Trash2, X, FileText, Clock, TriangleAlert, CircleCheck } from 'lucide-react';

function EditPrinterModal({ printer, onClose, onUpdate, onDelete }) {
    const [activeTab, setActiveTab] = useState('settings');
    
    const getLatestDate = (...dates) => {
        const validDates = dates.filter(d => d).map(d => new Date(d));
        if (validDates.length === 0) return '';
        const maxDate = new Date(Math.max.apply(null, validDates));
        return maxDate.toISOString().split('T')[0];
    };

    const [formData, setFormData] = useState({
        name: printer.name,
        model: printer.model,
        serial_number: printer.serial_number,
        current_print_hours: printer.current_print_hours,
        last_biweekly_date: getLatestDate(printer.last_enclosure_date, printer.last_extruder_date, printer.last_fans_date),
        last_monthly_date: getLatestDate(printer.last_carbon_rods_date),
        last_quarterly_date: getLatestDate(printer.last_lead_screws_date)
    });

    const [history, setHistory] = useState([]);
    const [loadingHistory, setLoadingHistory] = useState(false);
    const [generatingReport, setGeneratingReport] = useState(false);

    useEffect(() => {
        if (activeTab === 'history') {
            setLoadingHistory(true);
            fetch(`/api/printers/${printer.id}/history`)
                .then(res => res.json())
                .then(data => { setHistory(Array.isArray(data) ? data : []); setLoadingHistory(false); })
                .catch(err => setLoadingHistory(false));
        }
    }, [activeTab, printer.id]);

    const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

    const handleSave = async () => {
        if (!window.confirm("Save changes to this printer?")) return;
        await onUpdate(printer.id, formData);
    };

    const handleDelete = async () => {
        if (window.confirm(`?? ARE YOU SURE?\n\nThis will permanently delete "${printer.name}" and ALL its maintenance history logs.\n\nThis cannot be undone.`)) {
            await onDelete(printer.id);
        }
    };

    const handleGenerateReport = async () => {
        setGeneratingReport(true);
        try {
            const res = await fetch(`/api/printers/${printer.id}/report`, { method: 'POST' });
            const data = await res.json();
            if (res.ok) {
                alert(`? Report Generated!\n\nClick OK to open link.`);
                if (data.driveUrl) window.open(data.driveUrl, '_blank');
            } else {
                alert("Report Failed: " + data.message);
            }
        } catch (err) {
            alert("Network Error: " + err.message);
        } finally {
            setGeneratingReport(false);
        }
    };

    return (
        <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
            <div className="card" style={{ width: '100%', maxWidth: '600px', maxHeight: '90vh', overflowY: 'hidden', display:'flex', flexDirection:'column', padding:0, position: 'relative', backgroundColor: 'white' }}>
                
                {/* Header */}
                <div style={{ padding: '15px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#f8fafc' }}>
                    <h2 style={{ margin: 0, fontSize: '1.2rem' }}>Manage Printer</h2>
                    
                    {/* ?? IMPROVED CLOSE BUTTON */}
                    <button 
                        onClick={onClose} 
                        style={{ 
                            background: '#e2e8f0', border: 'none', cursor: 'pointer', 
                            padding: '6px', borderRadius: '50%', display: 'flex', 
                            alignItems: 'center', justifyContent: 'center', color: '#334155',
                            width: '32px', height: '32px'
                        }}
                        title="Close"
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Tabs */}
                <div style={{ display: 'flex', borderBottom: '1px solid #e2e8f0' }}>
                    <button onClick={() => setActiveTab('settings')} style={{ flex: 1, borderRadius: 0, background: activeTab === 'settings' ? 'white' : '#f1f5f9', color: activeTab === 'settings' ? 'var(--primary)' : '#64748b', borderBottom: activeTab === 'settings' ? '2px solid var(--primary)' : 'none', padding: '10px', cursor: 'pointer', border: 'none', borderRight: '1px solid #e2e8f0' }}>Settings</button>
                    <button onClick={() => setActiveTab('history')} style={{ flex: 1, borderRadius: 0, background: activeTab === 'history' ? 'white' : '#f1f5f9', color: activeTab === 'history' ? 'var(--primary)' : '#64748b', borderBottom: activeTab === 'history' ? '2px solid var(--primary)' : 'none', padding: '10px', cursor: 'pointer', border: 'none' }}>History</button>
                </div>

                {/* Content */}
                <div style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>
                    {activeTab === 'settings' && (
                        <>
                            <div className="form-section" style={{ background: '#fff', border: '1px solid #e2e8f0', padding: '15px', borderRadius: '8px', marginBottom: '15px' }}>
                                <h3 style={{ fontSize: '1rem', marginBottom: '10px' }}>Basic Info</h3>
                                <label>Name</label><input type="text" name="name" value={formData.name} onChange={handleChange} />
                                <label>Model</label><input type="text" name="model" value={formData.model} onChange={handleChange} />
                                <label>Serial Number</label><input type="text" name="serial_number" value={formData.serial_number} onChange={handleChange} />
                                <label>Current Hours</label><input type="number" name="current_print_hours" value={formData.current_print_hours} onChange={handleChange} />
                            </div>
                            <div className="form-section" style={{ background: '#fff7ed', border: '1px solid #fed7aa', padding: '15px', borderRadius: '8px', marginBottom: '15px' }}>
                                <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '10px' }}>
                                    <TriangleAlert size={18} color="#ea580c" />
                                    <h3 style={{ margin: 0, color: '#9a3412', fontSize: '1rem' }}>Maintenance Overrides</h3>
                                </div>
                                <p style={{ fontSize: '0.8rem', marginBottom: '10px', color: '#9a3412' }}>Reset timers manually. Clearing a date sets it to "Never Done".</p>
                                <label>Last Biweekly</label><input type="date" name="last_biweekly_date" value={formData.last_biweekly_date} onChange={handleChange} />
                                <label>Last Monthly</label><input type="date" name="last_monthly_date" value={formData.last_monthly_date} onChange={handleChange} />
                                <label>Last Quarterly</label><input type="date" name="last_quarterly_date" value={formData.last_quarterly_date} onChange={handleChange} />
                            </div>
                            <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                                <button onClick={handleSave} style={{ flex: 2, display: 'flex', gap: '8px' }}><Save size={18} /> Save Changes</button>
                                <button onClick={handleDelete} className="secondary" style={{ flex: 1, color: '#ef4444', borderColor: '#ef4444', display: 'flex', gap: '8px' }}><Trash2 size={18} /> Delete</button>
                            </div>
                        </>
                    )}

                    {activeTab === 'history' && (
                        <>
                            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '15px' }}>
                                <button onClick={handleGenerateReport} disabled={generatingReport} style={{ width: 'auto', display: 'flex', gap: '8px', fontSize: '0.9rem' }}>
                                    {generatingReport ? <Clock className="spin" size={16} /> : <FileText size={16} />}
                                    {generatingReport ? 'Generating PDF...' : 'Export PDF Report'}
                                </button>
                            </div>
                            {loadingHistory ? <p className="text-center">Loading...</p> : history.length === 0 ? <p className="text-center">No history.</p> : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                    {history.map(record => (
                                        <div key={record.id} style={{ padding: '10px', border: '1px solid #e2e8f0', borderRadius: '8px', background: 'white' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                                                <span style={{ fontWeight: 'bold', color: 'var(--primary)' }}>{new Date(record.maintenance_date).toLocaleDateString()}</span>
                                                <span style={{ fontSize: '0.8rem', color: '#64748b' }}>{new Date(record.maintenance_date).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</span>
                                            </div>
                                            <div style={{ fontSize: '0.9rem', marginBottom: '5px' }}><strong>Tech:</strong> {record.performed_by} � <strong>Type:</strong> {record.pm_type || 'Routine'}</div>
                                            {record.notes && <div style={{ fontSize: '0.85rem', color: '#334155', background: '#f1f5f9', padding: '5px', borderRadius: '4px' }}>{record.notes}</div>}
                                            <div style={{ marginTop: '5px', display: 'flex', gap: '10px', fontSize: '0.8rem', color: '#059669' }}>
                                                {record.is_synced === 1 && <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><CircleCheck size={12} /> Synced to QMS</span>}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
            <style>{`.spin { animation: spin 1s linear infinite; } @keyframes spin { 100% { transform: rotate(360deg); } }`}</style>
        </div>
    );
}

export default EditPrinterModal;